<template>
    <div v-click-outside="handleCancel" :class="classes" @click.stop="toggleEditMode">
        <template v-if="!editMode">
            <CompassSkeleton
                v-if="loading"
                size="x-large"
                :style="{ width: `${componentWidth}px` }"
            />
            <template v-else>
                <div
                    v-if="placeholder && !text"
                    ref="editTextPlaceholder"
                    class="edit-text__placeholder typographies-paragraph-medium-regular"
                >
                    {{ placeholder }}
                </div>
                <div v-else ref="editTextValue" class="edit-text__value">
                    <slot></slot>
                </div>
            </template>
        </template>
        <template v-else>
            <CompassInput
                ref="editTextInput"
                v-model="localValue"
                :class="contentTypographyClass"
                :style="{ maxWidth: `${componentWidth}px` }"
                @keypress="handleKeyPress"
                @keydown.enter.stop="handleValueUpdate"
                @keydown.esc.stop="handleCancel"
            >
                <template v-if="prefix" #prefix>
                    {{ numberFormat.currencysymbol }}
                </template>
                <template v-if="suffix" #suffix>
                    {{ numberFormat.currencysymbol }}
                </template>
            </CompassInput>
            <FloatingActions
                :float="floatActions"
                @cancel="handleCancel"
                @save="handleValueUpdate"
            />
        </template>
    </div>
</template>
<script>
import FloatingActions from 'vcomponents/common/editInPlace/FloatingActions/FloatingActions.vue';

export default {
    components: {
        FloatingActions,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        canEdit: {
            type: Boolean,
            default: true,
        },
        text: {
            type: String,
            default: '',
        },
        numberFormat: {
            type: Object,
            default: () => {},
        },
        contentTypographyClass: {
            type: String,
            default: '',
        },
        maxLength: {
            type: Number,
            default: null,
        },
        floatActions: {
            type: Boolean,
            default: true,
        },
        placeholder: {
            type: String,
            default: '',
        },
    },
    emits: ['update'],
    data() {
        return {
            editMode: false,
            localValue: this.text,
            componentWidth: 0,
            affixWidth: 18,
        };
    },
    computed: {
        classes() {
            return {
                'edit-text': true,
                'edit-text--editable': this.canEdit,
            };
        },
        prefix() {
            return (
                this.isNumberInput &&
                (this.numberFormat.currencypos === 'left' ||
                    this.numberFormat.currencypos === 'both')
            );
        },
        suffix() {
            return (
                this.isNumberInput &&
                (this.numberFormat.currencypos === 'right' ||
                    this.numberFormat.currencypos === 'both')
            );
        },
        isNumberInput() {
            return this.numberFormat && Object.keys(this.numberFormat).length > 0;
        },
    },
    watch: {
        editMode(newValue) {
            if (this.$refs.editTextValue) {
                this.setComponentWidth();
            } else if (this.$refs.editTextPlaceholder) {
                this.componentWidth = this.$refs.editTextPlaceholder.offsetWidth;
            }

            if (newValue && this.isNumberInput) {
                this.formatLocalValue();
            }
        },
    },
    created() {
        if (this.isNumberInput) {
            this.formatLocalValue();
        }
    },
    mounted() {
        this.$nextTick(() => {
            this.setComponentWidth();
        });
    },
    methods: {
        limitTextLength(event) {
            if (this.maxLength && this.localValue.length >= this.maxLength) {
                event.preventDefault();
            }
        },
        setComponentWidth() {
            const inputWidth = this.$refs.editTextValue.children[0].offsetWidth;
            const containerWidth = this.$refs.editTextValue.offsetWidth;

            if (
                (this.isNumberInput && inputWidth + this.affixWidth * 2 < containerWidth) ||
                inputWidth < containerWidth
            ) {
                this.componentWidth = containerWidth;
            } else {
                this.componentWidth = inputWidth;

                if (this.prefix || this.suffix) {
                    this.componentWidth += this.affixWidth * (this.prefix && this.suffix ? 2 : 1);
                }
            }
        },
        formatLocalValue() {
            this.localValue = this.$filters.numberFormat(this.localValue, false);
        },
        handleKeyPress($event) {
            this.limitTextLength($event);
            if (this.isNumberInput) {
                const keyPressed = $event.key;
                const regexKeysAllowed = new RegExp(
                    `[${this.numberFormat.decimals}${this.numberFormat.thousands}0-9]`,
                    'g',
                );

                if (!keyPressed.match(regexKeysAllowed)) {
                    $event.preventDefault();
                }
            }
        },
        toggleEditMode() {
            if (!this.editMode && this.canEdit && !window.getSelection().toString()) {
                this.editMode = true;
                this.$nextTick(() => {
                    this.$refs.editTextInput.setFocus();
                });
            }
        },
        handleCancel() {
            this.editMode = false;
            if (this.isNumberInput) {
                this.localValue = this.$filters.numberFormat(this.text, false);
            }
        },
        handleValueUpdate() {
            this.$emit('update', this.localValue);
            this.editMode = false;
        },
    },
};
</script>
<style scoped lang="scss">
.edit-text {
    position: relative;

    &__placeholder {
        color: transparent;

        &:hover {
            color: var(--colors-common-text-standard-disabled);
        }
    }

    &::v-deep {
        .compass-input {
            width: 100%;

            &.typographies-heading-h1 {
                padding-bottom: 1px;
                padding-top: 1px;
            }

            input {
                font-family: inherit;
                font-size: inherit;
                font-weight: inherit;
                line-height: inherit;
                min-width: 7ch;
            }
        }
    }

    &--editable {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-1);
        justify-content: center;
        min-height: var(--spacing-5);

        .edit-text__value,
        .edit-text__placeholder {
            &:before {
                content: '';
                background: var(--colors-common-background-primary-hover-alt);
                border-radius: 2px;
                bottom: 0;
                display: none;
                left: -4px;
                position: absolute;
                right: -4px;
                top: 0;
            }

            &:hover {
                &:before {
                    display: block;
                }
            }
        }
    }
}
</style>
